/**
 * Extracts images from phase messages, categorized by their display purpose.
 * This allows the preloader to enqueue each image with the correct ImageCategory,
 * ensuring the preloaded format matches what StrapiMedia will render.
 */

import { Message } from "@/types/api";
import { StrapiAsset } from "@/types/api";

export interface CategorizedImages {
    /** Large — room backgrounds, item assets */
    lg: StrapiAsset[];
    /** Medium — educational message images */
    md: StrapiAsset[];
    /** Small — meme icons, achievement icons */
    sm: StrapiAsset[];
}

export function emptyCategorizedImages(): CategorizedImages {
    return { lg: [], md: [], sm: [] };
}

/**
 * Extract and categorize all images from a list of messages.
 * If selectedRoom is provided, only includes item assets for that room variant.
 */
export function getCategorizedImagesFromMessages(
    messages: Message[],
    selectedRoom?: string,
): CategorizedImages {
    const result = emptyCategorizedImages();

    function extractFromMessage(message: Message, includeResponses: boolean) {
        if (!message) return;

        if (message.meme_icon?.icon) {
            result.sm.push(message.meme_icon.icon);
        }

        if (includeResponses && message.Response) {
            for (const response of message.Response) {
                if (!response) continue;
                if (response.item) {
                    if (selectedRoom) {
                        const asset =
                            selectedRoom === "a" ? response.item.asset_a : response.item.asset_b;
                        if (asset) result.lg.push(asset);
                    } else {
                        if (response.item.asset_a) result.lg.push(response.item.asset_a);
                        if (response.item.asset_b) result.lg.push(response.item.asset_b);
                    }
                }

                // TODO: resolve achievement documentIds against cacheStore and add
                // their images to result.icon here

                if (response.followUpMessage) {
                    extractFromMessage(response.followUpMessage, false);
                }
            }
        }

        if (message.educational_message?.image) {
            result.md.push(message.educational_message.image);
        }
    }

    for (const message of messages) {
        extractFromMessage(message, true);
    }

    return result;
}